import { CompetitionList, CupDetail } from '@/components/cups';
import { ROUTES } from '@/config';
import { Icon } from '@/core/icon';
import { Button, Dialog, DialogClose, DialogContent, DialogTitle, Skeleton, Typography } from '@/core/ui';
import { cn } from '@/lib';
import { useAppDispatch, useAppSelector } from '@/store';
import { API } from '@/store/api';
import { setCupModal } from '@/store/cups';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

const Competitions = () => {
  const { t } = useTranslation('translation');

  const [activeType, setActiveType] = useState<string | null>(null);

  const { isLoading, isFetching, data } = API.competitionTypesApi.useGetCompetitionTypesQuery();

  const competitionTypes = data?.competition_types ?? [];

  useEffect(() => {
    if (competitionTypes.length) {
      setActiveType(competitionTypes[0]);
    }
  }, [competitionTypes]);

  if (isLoading || isFetching || !competitionTypes.length) {
    return (
      <div className='w-full h-full flex justify-between'>
        <div className='flex flex-col space-y-5'>
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className='w-[400px] h-16 bg-chalk/10 rounded-full' />
          ))}
        </div>
        <div className='grid grid-cols-4 gap-7'>
          {Array.from({ length: 17 }).map((_, index) => (
            <div key={index} className='w-[290px] h-[108px] rounded-[17px]'>
              <Skeleton className='col-span-1 w-full h-full bg-chalk/10' />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className='flex basis-[30%] flex-col space-y-5'>
        {competitionTypes.map((typeName, index) => {
          const isActive = activeType === typeName;

          return (
            <button
              key={`ct-${index}`}
              type='button'
              onClick={() => setActiveType(typeName)}
              className={cn('w-[400px] py-5 rounded-full flex items-center justify-center', {
                'bg-danger text-white': isActive,
                'bg-white text-content2': !isActive,
              })}
            >
              <Typography className='text-[25px] uppercase text-inherit leading-[23.75px]'>
              {t(`competition_types.${typeName}`, { defaultValue: typeName })}
              </Typography>
            </button>
          );
        })}
      </div>

      <div className='basis-[70%] h-full'>
        <CompetitionList competitionType={activeType} />
      </div>
    </>
  );
};

const CompetitionWrapper = () => {
  const dispatch = useAppDispatch();
  const open = useAppSelector((s) => s.cup.cupModal);
  const { t } = useTranslation();

  const setModal = (val: boolean) => () => {
    dispatch(setCupModal(val));
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setModal(false)}>
        <DialogContent
          closeJsx={
            <DialogClose
              onClick={setModal(false)}
              className='absolute right-8 top-8 rounded-sm ring-offset-background transition-opacity hover:opacity-100 focus:outline-none disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground'
            >
              <Icon name={'X_ICON'} className='p-2 w-16 h-16' color='rgb(var(--color-danger))' />
              <span className='sr-only'>Close</span>
            </DialogClose>
          }
          className='bg-transparent flex justify-center items-center overflow-hidden shadow-none h-[80%] max-w-[90%] sm:rounded-2xl'
        >
          <DialogTitle />
          <CupDetail />
        </DialogContent>
      </Dialog>
      <div className='relative flex flex-col justify-between bg-content1 px-20 pt-10 pb-24 h-screen space-y-10'>
        <div className='flex items-center justify-between '>
          <Typography className='text-[30px] leading-[28.5px] tracking-[1.5px] text-chalk'>
            {t('cups.other')}
          </Typography>

          <Link to={ROUTES.MAIN} className={''}>
            <Button className='outline-none hover:bg-danger focus:bg-danger border-none flex px-7 py-8 bg-danger rounded-full items-center space-x-2'>
              <Icon className='h-5 w-5' name='CHEVRON_LEFT' />
              <Icon className='h-7 w-6' name='HOME' />
            </Button>
          </Link>
        </div>
        <div className='flex h-full items-start justify-between pt-12'>
          <Competitions />
        </div>

        <div className='block w-fit'>
          <Link to={ROUTES.CUPS.MAIN}>
            <Button className='outline-none hover:bg-danger focus:bg-danger justify-center border-none flex px-11 h-[96px] bg-danger rounded-full items-center space-x-2'>
              <Icon className='h-7 w-7' name='CHEVRON_LEFT' />
              <Typography className='text-[30px] leading-[28.5px] tracking-[1.5px] uppercase text-chalk'>
                {t('cups.back')}
              </Typography>
            </Button>
          </Link>
        </div>
      </div>
    </>
  );
};
export default CompetitionWrapper;
